/* eslint-disable no-undef */
import getMetaId from "./get-meta-id";
import { v4 as guid } from "uuid";

const eventId = () => `event_${guid()}`;
const getTotalPrice = (items) => Number(items.reduce((acc, item) => acc + item.quantity * item.price, 0).toFixed(2));

const trackEvent = async (event, data) => {
  dataLayer.push({
    event,
    event_id: eventId(),
    ...data,
  });
};

const trackEcommerceEvent = async (event, ecommerce) => {
  dataLayer.push({ ecommerce: null });
  trackEvent(event, { ecommerce: { ...ecommerce } });
};

const mapEcommerce = (products, list, dataSourceType) =>
  products?.map((product, i) => {
    let item;

    if (dataSourceType === "shopify") {
      item = {
        item_id: product?.sku,
        item_name: product?.productTitle,
        affiliation: "Olav",
        currency: process.env.GATSBY_CURRENCY,
        index: i,
        item_brand: "Olav",
        item_list_id: product?.variantId?.split("/").pop(),
        item_list_name: "You might also like this",
        item_variant: product?.variantTitle,
        price: +product?.priceV2?.amount,
        quantity: product?.quantity ?? 1
      };

    } else {
      let productData = product;

      if (list === "Cross Sell") {
        productData = product?.singleVariant[0]?.variant;
      } else if (list === "Product Category List") {
        productData = product?.reference?.product;
      }

      item = {
        item_id: productData?.store?.sku,
        item_name: productData?.store?.productTitle,
        affiliation: "Olav",
        currency: process.env.GATSBY_CURRENCY,
        index: productData?.index ?? i,
        item_brand: "Olav",
        item_list_id: productData?.store?.id?.split("-").pop(),
        item_list_name: "You might also like this",
        item_variant: productData?.title,
        price: +productData?.store?.price,
        quantity: productData?.quantity ?? 1
      };
    }

    if (product?.discounts?.length) {
      let itemDiscount = 0, coupons = [];
      product.discounts.forEach(discount => {
        itemDiscount += discount.amount;
        coupons.push(discount.title);
      });
      item.discount = itemDiscount;
      item.coupon = coupons.join(",");
    }

    return item;
  });

const trackAddToCart = ({ products, shopifyData }) => {
  const dataSourceType = shopifyData ? "shopify" : "local";
  const items = mapEcommerce([].concat(products), null, dataSourceType);
  trackEcommerceEvent("add_to_cart", {
    items,
    value: getTotalPrice(items),
    currency: process.env.GATSBY_CURRENCY,
  });
};

const trackRemoveFromCart = ({ products, shopifyData }) => {
  const dataSourceType = shopifyData ? "shopify" : "local";
  const items = mapEcommerce([].concat(products), null, dataSourceType);
  trackEcommerceEvent("remove_from_cart", {
    items,
    value: getTotalPrice(items),
    currency: process.env.GATSBY_CURRENCY,
  });
};

const trackViewCart = (cart, shopifyData) => {
  trackEcommerceEvent("view_cart", {
    value: +cart?.totalPriceV2?.amount,
    items: mapEcommerce(Object?.values(cart?.items), null, shopifyData),
    currency: process.env.GATSBY_CURRENCY,
  });
};

const trackBeginCheckout = (cart, shopifyData) => {
  const ecommerce = {
    value: +cart.totalPriceV2.amount,
    items: mapEcommerce(Object.values(cart.items), null, shopifyData),
    currency: process.env.GATSBY_CURRENCY,
  };
  if (cart?.discount?.code) ecommerce.coupon = cart.discount.code;
  trackEcommerceEvent("begin_checkout", ecommerce);
};


const trackDropdownClick = (status, text) => {
  trackEvent(
    "dropdown_click", {
      click_info: status,
      click_text: text
    });
};

const trackError = (error) => {
  trackEvent(
    "error", {
      error_reason: "email address",
      error_message: error
    });
};

const trackLogin = () => {
  trackEvent(
    "login", {
      login_reason: "user account",
      method: "email address and password"
    });
};

const trackSignUp = (type, method) => {
  trackEvent(
    "sign_up", {
      type,
      method
    });
};

const trackInviteFriend = (method) => {
  trackEvent("invite_friend", { method: method });
};

const trackSelectItem = (product, title, id) => {
  trackEcommerceEvent(
    "select_item", {
      item_list_id: id,
      item_list_name: title,
      items: mapEcommerce([product])
    }
  );
};

const shipmentTracking = () => {
  trackEvent("shipment_tracking");
};

const trackTabClick = (tab) => {
  trackEvent("tab_click", { click_text: tab });
};

const trackTeaserClick = (text, url) => {
  trackEvent("teaser_click", {
    click_text: text,
    link_url: url
  });
};

const trackViewItem = ({ product }) => {
  trackEcommerceEvent("view_item", { items: mapEcommerce([product]) });
};

const trackViewItemList = ({ title, products, list, id }) => {
  trackEcommerceEvent("view_item_list", {
    item_list_name: title ?? null,
    item_list_id: id ?? null,
    items: mapEcommerce(products, list),

  });
};

const trackPageView = ({ pageData }) => {
  const isUserLoggedIn = localStorage.getItem("isLoggedIn") === "true";
  trackEvent("pageView_trigger", {
    environment: window.location.origin === process.env.GATSBY_URL  ? "live-environment" : "test-environment",
    content_area: pageData?.area,
    content_group: pageData?.group,
    user_status: isUserLoggedIn ? "logged-in" : "not-logged-in",
    page_location: `${window.location.host}/${pageData?.handle}`,
    page_title: pageData?.title
  });
};

const trackEmailAddress = (email) => {
  trackEvent("user_data_push", { sha256_email_address: email });
};

const trackMenuClick = (menu) => {
  trackEvent("menu_click", {
    click_text: menu?.title,
    link_url: menu?.url ?? null
  });
};

const trackViewedProductKlaviyo = (product) => {
  const variant = product?.currentVariant ? product.currentVariant : product.variants[0];
  let _learnq = window._learnq || [];
  const item = {
    Name: product.title,
    ProductID: parseFloat(getMetaId(variant?.id)),
    Categories: variant?.store?.productType,
    URL: `${process.env.GATSBY_URL}/${product?.handle}/${variant?.handle?.current}`,
    Brand: "Olav",
    Price: parseFloat(variant?.store?.price),
    CompareAtPrice: parseFloat(variant?.store?.compareAtPrice)
  };
  _learnq.push(["track", "Viewed Product", item]);
};

const trackAddedToCartKlaviyo = (cart, item) => {
  const mappedItems = cart?.items?.map((it) => ({
    ProductID: it?.productId?.split("/").pop(),
    SKU: it?.sku,
    ProductName: it?.title,
    Quantity: it?.quantity,
    ItemPrice: +it?.price,
    RowTotal: it?.quantity * Number(it?.price),
    ProductURL: `${process.env.GATSBY_URL}/products/${it?.productHandle}`,
    ImageURL: it?.image?.src,
  }));

  const payload = {
    $value: Number(cart?.totalQuantity),
    AddedItemProductName: item?.store?.productTitle,
    AddedItemProductID: item?.store?.productId?.split("-")[1],
    AddedItemSKU: item?.store?.sku,
    AddedItemVariantID: getMetaId(item?.id),
    AddedItemPrice: item?.store?.price,
    AddedItemImageURL: item?.variantImg?.imgSrc?.url,
    AddedItemQuantity: item?.quantity ?? 1,
    CheckoutURL: cart?.webUrl,
    Items: mappedItems,
  };
  let _learnq = window._learnq || [];
  _learnq.push(["track", "Added to Cart", payload]);
};

async function triggerProductAddedEventMable(product) {
  const variant = product?.currentVariant || (product?.variants && product?.variants[0]) || product;
  const productHandle =  product?.handle || product?.store.productHandle;
  const variantId = getMetaId(variant?.id);
  const productStore = product?.store;

  try {
    const options = {
      apiKey: process.env.GATSBY_MABLE_API_KEY,
      product_handle: productHandle,
      variant_id: variantId,
      event_name: "Product Added",
      products: [{
        item_id: String(productStore?.id),
        item_brand: "Olav",
        item_category: productStore?.productType,
        item_name: product?.title,
        variant_id: variantId,
        variant_title: null,
        sku: variant?.store?.sku,
        quantity: 1,
        price: parseFloat(variant?.store?.price)
      }]
    };

    await window.wgs.triggerAtcEvent(options);

    console.log("Product Added event successfully triggered.");
  } catch (error) {
    console.error("Failed to trigger the Product Added event:", error);
  }
}


export {
  trackEvent,
  trackEcommerceEvent,
  trackAddToCart,
  trackRemoveFromCart,
  trackViewCart,
  trackBeginCheckout,
  trackViewItem,
  trackViewItemList,
  trackSelectItem,
  trackViewedProductKlaviyo,
  triggerProductAddedEventMable,
  trackDropdownClick,
  trackInviteFriend,
  trackError,
  trackLogin,
  trackSignUp,
  trackPageView,
  shipmentTracking,
  trackTabClick,
  trackTeaserClick, 
  trackEmailAddress, 
  trackMenuClick, 
  trackAddedToCartKlaviyo
};
